/**
 * Codes for questionnaire questions and answers (CJT-15710).
 *
 * formclient pushes every step to the dataLayer with the Salesforce field and the chosen Salesforce value
 * (`questionnaire.step.questionSalesforce` / `.answer`). Analytics should see neither the question nor the answer
 * in plain text, so each push gets `questionCode` (e.g. `Q07`) and, on the answered step, `answerCode` (a number)
 * from `questionnaireCodesMapping.ts`. The raw keys stay untouched for the tags that already use them.
 * Steps that aren't coded (form, page, skipped answer) get explicit nulls.
 *
 * Only `question` steps are coded. On `form` steps `answer` holds the entered contact data and is never touched.
 */
import { QuestionnaireCodesMapping } from '../interface/IQuestionnaireCodesMapping';
import mapping from './questionnaireCodesMapping';

export type UnmappedReport = (detail: { field: string; value?: string }) => void;

interface StepData {
  type?: string;
  questionSalesforce?: string | null;
  answer?: string | null;
  questionCode?: string | null;
  answerCode?: number | null;
  [key: string]: any;
}

function isCodable(step: StepData): boolean {
  return !!step && step.type === 'question' && !!step.questionSalesforce;
}

export function encodeQuestionnaireSteps(
  questionnaire: { step?: StepData; stepView?: StepData },
  report: UnmappedReport,
  config: QuestionnaireCodesMapping = mapping
): void {
  const { step, stepView } = questionnaire || {};

  // GTM merges every push into one data model: a key left out keeps the previous step's value. So both keys are
  // always written, null where they don't apply.
  if (step) {
    step.questionCode = null;
    step.answerCode = null;
  }

  if (stepView) {
    stepView.questionCode = null;
  }

  if (isCodable(step)) {
    const field = config.fields[step.questionSalesforce];
    const answered = step.answer !== null && step.answer !== undefined && step.answer !== '';

    step.questionCode = field ? field.code : config.unmapped_question_code;

    if (answered) {
      const known = !!field && field.values.hasOwnProperty(step.answer);
      step.answerCode = known ? field.values[step.answer] : config.unmapped_code;

      if (!known) {
        report({ field: step.questionSalesforce, value: step.answer });
      }
    } else if (!field) {
      report({ field: step.questionSalesforce });
    }
  }

  if (isCodable(stepView)) {
    const field = config.fields[stepView.questionSalesforce];
    stepView.questionCode = field ? field.code : config.unmapped_question_code;

    if (!field) {
      report({ field: stepView.questionSalesforce });
    }
  }
}
